import { useState, useCallback } from 'react';
import axios from 'axios';
import { EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import {
  Resource,
  ResourceFormData,
  ResourceListResponse
} from '../types/EducationHub';

interface ResourceFilters {
  category?: string;
  tags?: string | string[];
  limit?: number;
  offset?: number;
}

interface ResourceUploadResult {
  fileUrl: string;
  s3Key: string;
  originalFileName: string;
  fileSize: number;
  fileType: string;
}

// Custom hook for resource management
export const useResources = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getAdminHeaders = () => {
    const token = sessionStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchResources = useCallback(async (filters: ResourceFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<ResourceListResponse>(EDUCATION_ENDPOINTS.RESOURCES, {
        params: filters,
        headers: getAuthHeaders()
      });

      const data = response.data;
      setResources(data.resources || []);
      setTotalCount(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (err: any) {
      setResources([]);
      setTotalCount(0);
      setHasMore(false);

      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch resources');
        console.error('Error fetching resources:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getResourceById = useCallback(async (id: string): Promise<Resource | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<Resource>(`${EDUCATION_ENDPOINTS.RESOURCES}/${id}`, {
        headers: getAuthHeaders()
      });

      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch resource');
      console.error('Error fetching resource:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getResourceCategories = useCallback(async (): Promise<string[]> => {
    try {
      const response = await axios.get<{ categories: string[] }>(
        EDUCATION_ENDPOINTS.RESOURCES_CATEGORIES,
        { headers: getAuthHeaders() }
      );

      return response.data.categories || [];
    } catch (err: any) {
      console.error('Error fetching resource categories:', err);
      return [];
    }
  }, []);

  const getResourceTags = useCallback(async (): Promise<string[]> => {
    try {
      const response = await axios.get<{ tags: string[] }>(
        EDUCATION_ENDPOINTS.RESOURCES_TAGS,
        { headers: getAuthHeaders() }
      );

      return response.data.tags || [];
    } catch (err: any) {
      console.error('Error fetching resource tags:', err);
      return [];
    }
  }, []);

  const downloadResource = useCallback(async (resourceId: string, options?: { suggestedFilename?: string; fileType?: string }) => {
    try {
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			if (!token) throw new Error('No auth token found');

			const headers = {
				Authorization: `Bearer ${token}`,
			};
			// Make request for the file as a blob
			const response = await axios.get(
				EDUCATION_ENDPOINTS.RESOURCES_DOWNLOAD(resourceId),
				{ headers, responseType: 'blob' }
			);
      
      // 3. Trigger download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', options?.suggestedFilename || 'download');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading resource:', err);
    }
  }, []);

  // ── Super-admin operations ──────────────────────────────────────────────────

  const fetchAdminResources = useCallback(async (): Promise<Resource[]> => {
    const { data } = await axios.get(EDUCATION_ENDPOINTS.RESOURCES_ADMIN, { headers: getAdminHeaders() });
    return data.resources || [];
  }, []);

  // Step 1 of creating a resource: upload the file, get back fileUrl/s3Key/fileType/fileSize
  // to pass into createResource/updateResource.
  const uploadResourceFile = useCallback(async (file: File): Promise<ResourceUploadResult> => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(EDUCATION_ENDPOINTS.RESOURCES_UPLOAD, fd, { headers: getAdminHeaders() });
    return {
      fileUrl: data.fileUrl,
      s3Key: data.s3Key,
      originalFileName: data.originalFileName,
      fileSize: data.fileSize,
      fileType: data.fileType,
    };
  }, []);

  const createResource = useCallback(async (resourceData: ResourceFormData): Promise<Resource | null> => {
    setError(null);
    try {
      const { data } = await axios.post(EDUCATION_ENDPOINTS.RESOURCES, resourceData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create resource');
      throw err;
    }
  }, []);

  const updateResource = useCallback(async (id: string, resourceData: Partial<ResourceFormData>): Promise<Resource | null> => {
    setError(null);
    try {
      const { data } = await axios.put(EDUCATION_ENDPOINTS.RESOURCES_ITEM(id), resourceData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update resource');
      throw err;
    }
  }, []);

  const deleteResource = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await axios.delete(EDUCATION_ENDPOINTS.RESOURCES_ITEM(id), { headers: getAdminHeaders() });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete resource');
      throw err;
    }
  }, []);

  return {
    resources,
    loading,
    error,
    totalCount,
    hasMore,
    fetchResources,
    getResourceById,
    getResourceCategories,
    getResourceTags,
    downloadResource,
    fetchAdminResources,
    uploadResourceFile,
    createResource,
    updateResource,
    deleteResource
  };
};
